import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KILO } from '../../../constants/KILO_THEME'
import { useCalibrationCopy } from '../../../hooks/useCalibrationCopy'
import {
  type BodyMapNodeId,
  type UiDiscomfortLevel,
  severityToUiDiscomfort,
  uiDiscomfortToSeverity,
} from '../../../lib/calibration/onboardingConstants'
import { ONBOARDING, onboardingChipClass } from '../../../lib/onboarding/formStyles'
import { symptomPresets } from '../../../lib/i18nCatalog'
import type { OrthopedicInjuryEntry } from '../../../lib/calibrationTypes'

type InjuryNodeSheetProps = {
  nodeId: BodyMapNodeId
  nodeLabel: string
  initial: OrthopedicInjuryEntry | null
  onSave: (entry: OrthopedicInjuryEntry | null) => void
  onClose: () => void
}

const InjuryNodeSheet = ({ nodeId, nodeLabel, initial, onSave, onClose }: InjuryNodeSheetProps) => {
  const { t } = useTranslation()
  const { discomfortLevels } = useCalibrationCopy()
  const [discomfort, setDiscomfort] = useState<UiDiscomfortLevel>(
    initial ? severityToUiDiscomfort(initial.severity) : 'low',
  )
  const [tags, setTags] = useState<string[]>(initial?.tags ?? [])
  const [notes, setNotes] = useState(initial?.notes ?? '')

  const presets = useMemo(() => symptomPresets(t, nodeId), [t, nodeId])

  useEffect(() => {
    setDiscomfort(initial ? severityToUiDiscomfort(initial.severity) : 'low')
    setTags(initial?.tags ?? [])
    setNotes(initial?.notes ?? '')
  }, [initial, nodeId])

  function toggleTag(tag: string) {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
  }

  function handleSave() {
    onSave({
      severity: uiDiscomfortToSeverity(discomfort),
      tags: tags.length > 0 ? tags : undefined,
      notes: notes.trim() || undefined,
      since: initial?.since ?? new Date().toISOString().slice(0, 10),
    })
    onClose()
  }

  function handleClear() {
    onSave(null)
    onClose()
  }

  return (
    <div className={`fixed inset-0 z-50 flex items-end justify-center ${KILO.overlay} p-4 backdrop-blur-sm sm:items-center`}>
      <div
        className={`${KILO.bg.card} w-full max-w-md ${KILO.radius.card} ${KILO.border.default} p-5 shadow-2xl transition-transform`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="injury-sheet-title"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className={KILO.meta}>{t('onboarding.calibration.injurySheet.meta')}</p>
            <h3 id="injury-sheet-title" className={ONBOARDING.subheading}>
              {nodeLabel}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-full border border-kilo-line px-2.5 py-1 text-xs ${ONBOARDING.muted} hover:text-kilo-text`}
          >
            {t('common.close')}
          </button>
        </div>

        <div className="space-y-5">
          <div>
            <p className={`mb-2 text-sm font-medium ${ONBOARDING.label}`}>{t('onboarding.calibration.injurySheet.discomfortLevel')}</p>
            <div className="grid grid-cols-3 gap-2">
              {discomfortLevels.map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => setDiscomfort(level.value)}
                  className={`rounded-xl border-2 px-3 py-2 text-sm font-semibold transition ${
                    discomfort === level.value ? level.tone : ONBOARDING.chipInactive
                  }`}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className={`mb-2 text-sm font-medium ${ONBOARDING.label}`}>{t('onboarding.calibration.injurySheet.structuralSymptoms')}</p>
            <div className="flex flex-wrap gap-2">
              {presets.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={onboardingChipClass(tags.includes(tag))}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <label className="block space-y-2">
            <span className={ONBOARDING.label}>{t('onboarding.calibration.injurySheet.additionalNotes')}</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={t('onboarding.calibration.injurySheet.notesPlaceholder')}
              className={ONBOARDING.textarea}
            />
          </label>
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 rounded-2xl bg-kilo-accent py-3 text-sm font-bold text-black"
          >
            {t('onboarding.calibration.injurySheet.saveNode')}
          </button>
          {initial ? (
            <button
              type="button"
              onClick={handleClear}
              className="rounded-2xl border border-kilo-danger-border bg-kilo-danger-bg px-4 py-3 text-sm font-semibold text-kilo-danger"
            >
              {t('onboarding.calibration.injurySheet.clear')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default InjuryNodeSheet
